import { useState } from "react";
import Section from "@/components/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Mail, Facebook, Instagram, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

const allTimeSlots = [
  "7h00", "8h00", "9h00", "10h00", "11h00", "12h15", "16h30", "17h30", "18h30", "19h30"
];

const getTimeSlotsForDay = (date: Date | undefined): string[] => {
  if (!date) return allTimeSlots;
  const day = date.getDay(); // 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
  if (day === 6) return ["9h00", "10h00", "11h00"]; // Samedi
  if (day === 2) return allTimeSlots.filter(t => t !== "7h00" && t !== "8h00"); // Mardi
  if (day === 5) return allTimeSlots.filter(t => t !== "19h30"); // Vendredi
  return allTimeSlots;
};

const tabColors: Record<string, { active: string; label: string }> = {
  essai: { active: "bg-success text-white", label: "Essai gratuit" },
  dropin: { active: "bg-gold text-gold-foreground", label: "Drop-in (15 € HT)" },
  message: { active: "bg-charcoal text-charcoal-foreground", label: "Nous écrire" },
};

export default function ContactSection() {
  const [formType, setFormType] = useState<"essai" | "dropin" | "message">("essai");
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>("");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = (data.get("website_url") as string || "").trim();
    if (honeypot) return;

    const lastname = (data.get("lastname") as string || "").trim();
    const firstname = (data.get("firstname") as string || "").trim();
    const email = (data.get("email") as string || "").trim();
    const phone = (data.get("phone") as string || "").trim();
    const message = (data.get("message") as string || "").trim();

    if (!lastname || !firstname || !email || !phone) return;

    const slotStr = selectedDate
      ? `${format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}${selectedTime ? ` à ${selectedTime}` : ""}`
      : "";

    const subject = formType === "dropin" ? "réservation Drop-in" : formType === "essai" ? "Réservation essai gratuit" : "Message depuis le site";
    const bodyLines = [
      `Nom : ${lastname}`,
      `Prénom : ${firstname}`,
      `Email : ${email}`,
      `Téléphone : ${phone}`,
      slotStr ? `Créneau souhaité : ${slotStr}` : "",
      message ? `\nMessage :\n${message}` : "",
    ].filter(Boolean).join("\n");

    const mailtoUrl = `mailto:contact@crossfitchistera.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyLines)}`;
    window.location.href = mailtoUrl;
  };

  const showCalendar = formType !== "message";

  return (
    <Section id="contact">
      <div className="text-center mb-12">
        <span className="inline-block text-sm font-semibold text-gold tracking-wider mb-2 font-body normal-case">On vous attend avec impatience !</span>
        <h2 className="text-4xl md:text-5xl text-foreground">
          {formType === "dropin" ? "Réserver un drop-in" : formType === "essai" ? "Je réserve mon essai gratuit" : "Nous écrire"}
        </h2>
      </div>

      <div className="grid lg:grid-cols-5 gap-12 max-w-5xl mx-auto">
        <form onSubmit={handleSubmit} className="lg:col-span-3 bg-card border border-border rounded-lg p-8 space-y-5">
          {/* Toggle tabs with distinct colors */}
          <div className="flex gap-2 mb-2 flex-wrap">
            {(["essai", "dropin", "message"] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => { setFormType(type); setSelectedDate(undefined); setSelectedTime(""); }}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors font-body normal-case ${
                  formType === type
                    ? tabColors[type].active
                    : "bg-muted text-muted-foreground hover:bg-muted/80"
                }`}
              >
                {tabColors[type].label}
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="lastname" className="font-body normal-case">Nom *</Label>
              <Input id="lastname" name="lastname" required maxLength={100} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="firstname" className="font-body normal-case">Prénom *</Label>
              <Input id="firstname" name="firstname" required maxLength={100} className="mt-1" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="email" className="font-body normal-case">Email *</Label>
              <Input id="email" name="email" type="email" required maxLength={255} className="mt-1" />
            </div>
            <div>
              <Label htmlFor="phone" className="font-body normal-case">Téléphone *</Label>
              <Input id="phone" name="phone" type="tel" required maxLength={20} className="mt-1" />
            </div>
          </div>

          {/* Honeypot */}
          <div className="absolute opacity-0 pointer-events-none h-0 overflow-hidden" aria-hidden="true" tabIndex={-1}>
            <label htmlFor="website_url">Ne pas remplir</label>
            <input type="text" id="website_url" name="website_url" tabIndex={-1} autoComplete="off" />
          </div>

          {/* Calendar + Message side by side on desktop */}
          {showCalendar ? (
            <>
              <div className="flex flex-col">
                <Label className="font-body normal-case mb-3 block text-base font-semibold">Sélectionnez la date</Label>
                <div className="bg-background border border-border rounded-lg p-4 flex items-center justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(d) => { setSelectedDate(d); setSelectedTime(""); }}
                    locale={fr}
                    disabled={(date) => date < new Date() || date.getDay() === 0}
                    className={cn("p-0 pointer-events-auto")}
                  />
                </div>
              </div>

              {selectedDate && (
                <div>
                  <Label className="font-body normal-case mb-3 block text-base font-semibold">
                    <Clock size={14} className="inline mr-1.5" />
                    Sélectionnez l'heure
                  </Label>
                  <div className="grid grid-cols-3 gap-2">
                    {getTimeSlotsForDay(selectedDate).map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => setSelectedTime(t)}
                        className={`py-3 rounded-lg text-sm font-semibold transition-colors border font-body normal-case ${
                          selectedTime === t
                            ? "bg-cta text-cta-foreground border-cta"
                            : "bg-background text-foreground border-border hover:border-gold hover:text-gold"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {selectedDate && selectedTime && (
                <div className="bg-gold/10 border border-gold/20 rounded-lg p-3 text-center">
                  <p className="text-sm text-foreground font-body normal-case">
                    📅 Le <strong>{format(selectedDate, "EEEE d MMMM yyyy", { locale: fr })}</strong> à <strong>{selectedTime}</strong>
                  </p>
                </div>
              )}

              <div>
                <Label htmlFor="message" className="font-body normal-case">Message</Label>
                <Textarea id="message" name="message" maxLength={1000} placeholder="Dites-nous en plus sur vous, votre niveau, vos objectifs…" className="mt-1" />
              </div>
            </>
          ) : (
            <div>
              <Label htmlFor="message" className="font-body normal-case">Message</Label>
              <Textarea id="message" name="message" rows={4} maxLength={1000} placeholder="Écrivez votre message" className="mt-1" />
            </div>
          )}

          <Button type="submit" className="w-full bg-cta text-cta-foreground hover:bg-cta/90 rounded-lg font-semibold py-6 text-base">
            {formType === "dropin" ? "Demander mon drop-in" : formType === "essai" ? "Réserver mon essai gratuit" : "Envoyer mon message"}
          </Button>

          <p className="text-xs text-muted-foreground font-body normal-case leading-relaxed">
            Les informations recueillies via ce formulaire sont traitées par MOJO (SARL) exploitant CrossFit Chistera, 
            pour répondre à votre demande. Elles sont conservées 12 mois maximum. 
            Vous pouvez exercer vos droits à <a href="mailto:contact@crossfitchistera.com" className="text-gold hover:underline">contact@crossfitchistera.com</a>. 
            Voir notre <a href="/legal/rgpd" className="text-gold hover:underline">politique RGPD</a>.
          </p>
        </form>

        <div className="lg:col-span-2 flex flex-col gap-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-xl text-foreground mb-4">Infos pratiques</h3>
            <ul className="space-y-4 text-sm font-body normal-case">
              <li className="flex items-start gap-3">
                <MapPin size={18} className="text-gold shrink-0 mt-0.5" />
                <div>
                  <strong className="text-foreground">Adresse</strong>
                  <p className="text-muted-foreground">37 rue Pierre et Marie Curie<br/>69780 Saint-Pierre-de-Chandieu</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Mail size={18} className="text-gold shrink-0 mt-0.5" />
                <div>
                  <strong className="text-foreground">Email</strong>
                  <p className="text-muted-foreground">
                    <a href="mailto:contact@crossfitchistera.com" className="hover:text-gold transition-colors">contact@crossfitchistera.com</a>
                  </p>
                </div>
              </li>
            </ul>
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-xl text-foreground mb-4">Suivez-nous</h3>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/share/1DyTHxfnuD/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors font-body normal-case">
                <Facebook size={20} /> Facebook
              </a>
              <a href="https://www.instagram.com/crossfitchistera?igsh=bzJocjNtZGI1cDF3" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-gold transition-colors font-body normal-case">
                <Instagram size={20} /> Instagram
              </a>
            </div>
          </div>

          {/* Google Maps */}
          <div className="rounded-lg overflow-hidden border border-border">
            <iframe
              src="https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=37+Rue+Pierre+et+Marie+Curie,69780+Saint-Pierre-de-Chandieu,France&zoom=16"
              width="100%"
              height="250"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="CrossFit Chistera - Google Maps"
            />
          </div>
        </div>
      </div>
    </Section>
  );
}
